/**
 * Currency conversion utility that converts between currencies using EUR as a base currency.
 *
 * To add support for more currency pairs:
 * 1. Create a new conversion rate file (e.g., `eur-gbp.ts`) following the same structure:
 *    - Export `EUR_GBP_FALLBACK_RATE` constant
 *    - Export `EUR_GBP_HISTORY` object with date keys in format 'YYYY-DD-MM' and rate values
 * 2. Import the new constants in this file
 * 3. Add a new entry to the `CONVERSION_RATES` object with key 'EUR_GBP' (or appropriate pair)
 *
 * The utility automatically handles:
 * - Reverse conversions (USD->EUR if EUR->USD exists)
 * - Date-based rate lookups with fallback to default rates
 * - Conversions between any two currencies via EUR as intermediate
 */
import { EUR_USD_FALLBACK_RATE, EUR_USD_HISTORY } from './eur-usd'
import { EUR_CHF_FALLBACK_RATE, EUR_CHF_HISTORY } from './eur-chf'

type ConversionRateProvider = {
    history: Record<string, number>
    fallbackRate: number
}

const CONVERSION_RATES: Record<string, ConversionRateProvider> = {
    EUR_USD: {
        history: EUR_USD_HISTORY,
        fallbackRate: EUR_USD_FALLBACK_RATE,
    },
    EUR_CHF: {
        history: EUR_CHF_HISTORY,
        fallbackRate: EUR_CHF_FALLBACK_RATE,
    },
}

function getCurrencyPairKey(fromCurrency: string, toCurrency: string): string {
    return `${fromCurrency}_${toCurrency}`
}

function getReverseCurrencyPairKey(
    fromCurrency: string,
    toCurrency: string,
): string {
    return `${toCurrency}_${fromCurrency}`
}

function formatDateKey(date: Date): string {
    const year = date.getFullYear()
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${year}-${day}-${month}`
}

function getConversionRate(
    fromCurrency: string,
    toCurrency: string,
    date: Date,
): number | null {
    const pairKey = getCurrencyPairKey(fromCurrency, toCurrency)
    const provider = CONVERSION_RATES[pairKey]

    if (provider) {
        const rate = getConversionRateFromProvider(provider, date)
        return rate
    }

    // Try reverse conversion
    const reversePairKey = getReverseCurrencyPairKey(fromCurrency, toCurrency)
    const reverseProvider = CONVERSION_RATES[reversePairKey]

    if (reverseProvider) {
        const reverseRate = getConversionRateFromProvider(reverseProvider, date)
        return 1 / reverseRate
    }

    return null
}

function getConversionRateFromProvider(
    provider: ConversionRateProvider,
    date: Date,
): number {
    for (let daysBack = 0; daysBack <= 5; daysBack++) {
        const checkDate = new Date(date)
        checkDate.setDate(checkDate.getDate() - daysBack)
        const dateKey = formatDateKey(checkDate)

        const rate = provider.history[dateKey]
        if (rate !== undefined) {
            return rate
        }
    }

    return provider.fallbackRate
}

function findConversionPath(
    fromCurrency: string,
    toCurrency: string,
    date: Date,
): number | null {
    // Try direct conversion first
    const directRate = getConversionRate(fromCurrency, toCurrency, date)
    if (directRate !== null) {
        return directRate
    }

    // Find available base currencies from CONVERSION_RATES keys
    const availableBases = new Set<string>()
    for (const key of Object.keys(CONVERSION_RATES)) {
        const [base] = key.split('_')
        availableBases.add(base)
    }

    // Try converting via each available base currency
    for (const baseCurrency of availableBases) {
        if (baseCurrency === fromCurrency || baseCurrency === toCurrency) {
            continue
        }

        const rateToBase = getConversionRate(fromCurrency, baseCurrency, date)
        const rateFromBase = getConversionRate(baseCurrency, toCurrency, date)

        if (rateToBase !== null && rateFromBase !== null) {
            return rateToBase * rateFromBase
        }
    }

    return null
}

export function convertCurrency(
    amount: number,
    fromCurrency: string | null,
    toCurrency: string | null,
    date: Date | number,
): number {
    if (!fromCurrency || !toCurrency) {
        return amount
    }

    const normalizedFrom = fromCurrency.toUpperCase()
    const normalizedTo = toCurrency.toUpperCase()

    if (normalizedFrom === normalizedTo) {
        return amount
    }

    const dateObj = typeof date === 'number' ? new Date(date) : date

    const rate = findConversionPath(normalizedFrom, normalizedTo, dateObj)
    if (rate === null) {
        return amount
    }

    return amount * rate
}
